import { Injectable } from '@angular/core';

import { HttpClient } from '@angular/common/http';

import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Versione } from 'src/app/models/versione';
import { Modulo } from '../../models/modulo';
import { Env } from './env';
import { EnvResolver } from './env-resolver';

/**
 * Servizio che fornisce impostazioni dipendenti dall'environment
 * Contatta uno Spring Cloud Config Server
 */
@Injectable( { providedIn: 'root' })
export class EnvService {

    public API;
    public SERVER_REQUEST_TIMEOUT;
    public PWA_URL;
    public OAUTH_CLIENT_ID;
    public OAUTH_AUTHORIZE_URL;
    public OIDC_ISSUER;
    public OIDC_SECRET;
    public OIDC_CLIENT_ID;
    public OIDC_SCOPE;
    public OIDC_RESPTYPE;

    /** impostazioni geoloc in ms */
    public GEOLOC_TIMEOUT = 30000;
    public GEOLOC_MAXAGE = 60000;

    /** per le notifiche push */
    public ONESIGNAL_ID;
    public GOOGLE_PROJECT_ID;
    public LAST_VERSION;
    public STORE_URL;

    /** moduli attivi */
    public moduli: Modulo[];

    public versioni: Versione[];
    private env: Env;
    private configurazioni;



    // eventuale introduzione un proxy locale per aggirare il CORS
    // private CONFIG_SERVER_URL = 'configui';

    private CONFIG_SERVER_URL = 'https://mobile-rilfreq-configserver-ui.app.cineca.it';

    constructor(public http: HttpClient,
                private envResolver: EnvResolver,
                public platform: Platform,
                private translateService: TranslateService) {
        console.log('Hello EnvService Service Provider');
        this.moduli = [];
        this.versioni = [];

    }

    public ready(): Promise<Env> {
        const thisService = this;
        return new Promise(function (resolve, reject) {
            thisService.envResolver.init().then((env) => {
                thisService.env = env;
                console.log('Environment: ' + env.getInfo());
                resolve(env);
            });
        });
    }

    /**
     * ritorna una stringa con info sull'env corrente utile per log o da mettere a video
     */
    public getInfo(): string {
        return this.env.getInfo();
    }

    public isLocal(): boolean {
        return this.env.env === 'local';
    }

    public isAPP(): boolean {
        return this.env.isAPP();
    }

    public isPWA(): boolean {
        return this.env.isPWA();
    }

    public getTenant(): string {
        return this.env ? this.env.tenant : 'test';
    }

    public getExtBaseUrl(): string {
        return this.env.getExtBaseUrl();
    }

    getValue(key: string) {
        for (const element of this.configurazioni.propertySources) {
            if (element.source[key]) {
                return element.source[key];
            }
        }

        return undefined;
    }

    getModuli(): Modulo[] {
        return this.moduli.filter((page) => this.isPWA() ? page.pwa : page.app);
    }


    caricaConfigurazioni(): Observable<any> {
        console.log('configurazioni');

        if (this.configurazioni) {
            console.log('this.configurazioni ' + this.configurazioni);
            return of(this.configurazioni);
        } else {
            let url = `${this.CONFIG_SERVER_URL}/${this.env.tenant}/${this.env.env}`;
            if (this.env.version) {
                url += `,${this.env.version}`;
            }

            return this.http.get<any>(url).pipe(
                        map( this.parseConfigurazioni, this ) ) ;


        }
    }

    private parseConfigurazioni(confs: any) {

        this.configurazioni = confs;

        if (this.configurazioni) {

            this.API = this.getValue('server.api');
            if (this.API && !this.API.endsWith('/')) {
                this.API += '/';
            }
            this.SERVER_REQUEST_TIMEOUT = this.getValue('server.timeout');
            this.OAUTH_AUTHORIZE_URL = this.getValue('oauth.authorize_url');
            this.OIDC_ISSUER = this.getValue('oauth.oidc_issuer');
            this.OIDC_SECRET = this.getValue('oauth.oidc_secret');
            this.OIDC_CLIENT_ID = this.getValue('oauth.oidc_client_id');
            this.OIDC_RESPTYPE = this.getValue('oauth.oidc_resptype');
            if (!this.OIDC_RESPTYPE) {
                this.OIDC_RESPTYPE = 'code';
            }
            this.OIDC_SCOPE = this.getValue('oauth.oidc_scope');
            if (!this.OIDC_SCOPE) {
                this.OIDC_SCOPE = 'any offline_access';
            }
            this.OAUTH_CLIENT_ID = this.getValue('oauth.client_id');
            this.PWA_URL = this.getValue('pwa.url');
            this.ONESIGNAL_ID = this.getValue('onesignal.appid');
            this.GOOGLE_PROJECT_ID = this.getValue('google.projectnumber');
            if (this.getValue('geoloc.timeout')) {
                this.GEOLOC_TIMEOUT = this.getValue('geoloc.timeout');
            }
            if (this.getValue('geoloc.maxage')) {
            this.GEOLOC_MAXAGE = this.getValue('geoloc.maxage');
            }

            this.caricaVersioniCorrenti();

            if ( this.versioni.length > 0 ) {
                if (this.platform.is('ios')) {
                    if (this.versioni[1].version) {
                        this.LAST_VERSION = this.versioni[1].version;
                        this.STORE_URL = this.versioni[1].store_url;
                    }
                } else {
                    if (this.versioni[0].version) {
                        this.LAST_VERSION = this.versioni[0].version;
                        this.STORE_URL = this.versioni[0].store_url;
                    }
                }
            }
            if (!this.LAST_VERSION) {
                this.LAST_VERSION = '0.0.0';
            }

            this.parseModuli();

        }
    }

    /**
     * esempio di json da parsare:
     *
        "moduli[0].type": "page",
        "moduli[0].code": "DASHBOARD",
        "moduli[0].component": "Home",
        "moduli[0].pwa": true,
        "moduli[0].app": true,
        "moduli[1].type": "page",
        "moduli[1].code": "RILEVAZIONE",
        "moduli[1].component": "RilevazioneHomePage",
        "moduli[1].pwa": true,
        "moduli[1].app": true,
     */
    private parseModuli() {

        const sorgenteModuli = this.cercaDefinizioneModuli();

        if (!sorgenteModuli) {
            throw new Error('configurazioni dei moduli non trovata');
        }

        for (let i = 0; i < 1000; i++) { // per scrupolo max 1000 cicli
            if (sorgenteModuli['moduli[' + i + '].type']) {
                // console.log('trovato modulo '+i);
                const modulo = {
                    code: sorgenteModuli['moduli[' + i + '].code'],
                    title: sorgenteModuli['moduli[' + i + '].title'],
                    type: sorgenteModuli['moduli[' + i + '].type'],
                    component: sorgenteModuli['moduli[' + i + '].component'],
                    pwa: sorgenteModuli['moduli[' + i + '].pwa'],
                    app: sorgenteModuli['moduli[' + i + '].app'],
                    url: sorgenteModuli['moduli[' + i + '].url'],
                    ruolo: sorgenteModuli['moduli[' + i + '].ruolo']
                };
                this.moduli.push(modulo);
            } else {
                return;
            }
        }
    }

    private caricaVersioniCorrenti() {
        for (const element of this.configurazioni.propertySources) {
            if (element.source['current-version[0].platform']) {

                for (let i = 0; i < 2; i++) { // 2 piattaforme, android e ios

                    const versione = {
                        platform: element.source['current-version[' + i + '].platform'],
                        version: element.source['current-version[' + i + '].version'],
                        store_url: element.source['current-version[' + i + '].store_url'],
                    };
                    this.versioni.push(versione);
                }

            }
        }

    }
    /**
     * ricerca la sezione dei moduli
     * nota: li leggiamo solo dal primo source per evitare conflitti
     */
    private cercaDefinizioneModuli() {

        for (const element of this.configurazioni.propertySources) {
            if (element.source['moduli[0].type']) {
                return element.source;
            }
        }

        return undefined;
    }

    changeLanguage(lang: string) {
        console.log('Imposto la lingua ' + lang);
        this.translateService.use(lang);
      }

    refreshJWT(accessToken: string): Observable<any> {
        if (this.getExtBaseUrl()) {
            let url = '';
            if (this.env.env === 'local') { // usa il proxy locale
                url = `webesse3-${this.env.env}/jwt/refresh.do?jwt=${accessToken}`;
            } else {
                url = `${this.getExtBaseUrl()}/jwt/refresh.do?jwt=${accessToken}`;
            }
            // window.location.href = url;
            return this.http.get<any>(url).pipe(
                map( r => this.parseToken(r) ) ) ;
        }
        return of(undefined);
    }

    private parseToken(newTokenResponse: any): string {
        console.log('ping token response ' , newTokenResponse);
        return newTokenResponse.jwt;
    }

    refreshSessione(jsessionId?: string): Observable<any> {
        if (this.getExtBaseUrl()) {
            let url = '';
            if (this.env.env === 'local') { // usa il proxy locale
                url = `webesse3-${this.env.env}/json/ping.do;jsessionid=${jsessionId}`;
            } else {
                url = `${this.getExtBaseUrl()}/json/ping.do;jsessionid=${jsessionId}`;
            }
            // window.location.href = url;
            return this.http.get<any>(url).pipe(
                map( r => this.parsePingResponse(r) ) ) ;
        }
        // console.log('ping return undefined');
        return of(undefined);
    }

    private parsePingResponse(pingResponse: any): any {
        return pingResponse;
    }


}
